import React, { useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm

const formatHour = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'bg-red-500/15 border-red-500 text-red-700';
    case 'high': return 'bg-orange-500/15 border-orange-500 text-orange-700';
    case 'medium': return 'bg-yellow-500/15 border-yellow-500 text-yellow-700';
    case 'low': return 'bg-green-500/15 border-green-500 text-green-700';
    default: return 'bg-primary/10 border-primary text-primary';
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-success/15 border-success text-success';
    case 'in progress': return 'bg-warning/15 border-warning text-warning';
    case 'pending': return 'bg-muted border-muted-foreground text-muted-foreground';
    case 'on hold': return 'bg-secondary/15 border-secondary text-secondary-foreground';
    default: return 'bg-primary/10 border-primary text-primary';
  }
};

const isSameDay = (a, b) =>
  a?.getDate() === b?.getDate() &&
  a?.getMonth() === b?.getMonth() &&
  a?.getFullYear() === b?.getFullYear();

const WeeklyCalendar = ({ tickets = [], onTicketClick, onTicketReschedule }) => {
  const [viewMode, setViewMode] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [draggingTicket, setDraggingTicket] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dragSuccess, setDragSuccess] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // ── helpers ────────────────────────────────────────────────────────────────
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthNamesShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const fullDayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  const getWeekDays = (date) => {
    const d = new Date(date);
    const day = d?.getDay();
    const sunday = new Date(d);
    sunday?.setDate(d?.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const wd = new Date(sunday);
      wd?.setDate(sunday?.getDate() + i);
      return wd;
    });
  };

  const isToday = (date) => isSameDay(date, new Date());

  const getTicketsForDate = (date) =>
    tickets?.filter(ticket => {
      if (!ticket?.scheduledDate) return false;
      return isSameDay(new Date(ticket.scheduledDate), date);
    });

  const getTicketsForDateAndHour = (date, hour) =>
    getTicketsForDate(date)?.filter(ticket => {
      const h = new Date(ticket.scheduledDate)?.getHours();
      return h === hour;
    });

  const getUntimedTicketsForDate = (date) =>
    getTicketsForDate(date)?.filter(ticket => {
      if (!ticket?.scheduledDate) return true;
      const h = new Date(ticket.scheduledDate)?.getHours();
      return h < 7 || h > 19;
    });

  const getTicketsForMonthDay = (day) =>
    tickets?.filter(ticket => {
      if (!ticket?.scheduledDate) return false;
      const d = new Date(ticket.scheduledDate);
      return d?.getDate() === day &&
        d?.getMonth() === currentMonth?.getMonth() &&
        d?.getFullYear() === currentMonth?.getFullYear();
    });

  // ── navigation ─────────────────────────────────────────────────────────────
  const handlePrev = () => {
    if (viewMode === 'day') {
      const d = new Date(selectedDate);
      d?.setDate(d?.getDate() - 1);
      setSelectedDate(d);
    } else if (viewMode === 'week') {
      const d = new Date(selectedDate);
      d?.setDate(d?.getDate() - 7);
      setSelectedDate(d);
    } else {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      const d = new Date(selectedDate);
      d?.setDate(d?.getDate() + 1);
      setSelectedDate(d);
    } else if (viewMode === 'week') {
      const d = new Date(selectedDate);
      d?.setDate(d?.getDate() + 7);
      setSelectedDate(d);
    } else {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today);
  };

  const getHeaderLabel = () => {
    if (viewMode === 'day') {
      return `${monthNames?.[selectedDate?.getMonth()]} ${selectedDate?.getDate()}, ${selectedDate?.getFullYear()}`;
    }
    if (viewMode === 'week') {
      const wDays = getWeekDays(selectedDate);
      const start = wDays?.[0];
      const end = wDays?.[6];
      if (start?.getMonth() === end?.getMonth()) {
        return `${monthNamesShort?.[start?.getMonth()]} ${start?.getDate()} – ${end?.getDate()}, ${start?.getFullYear()}`;
      }
      return `${monthNamesShort?.[start?.getMonth()]} ${start?.getDate()} – ${monthNamesShort?.[end?.getMonth()]} ${end?.getDate()}, ${end?.getFullYear()}`;
    }
    return `${monthNames?.[currentMonth?.getMonth()]} ${currentMonth?.getFullYear()}`;
  };

  const totalScheduled = tickets?.filter(t => t?.scheduledDate)?.length;

  // ── drag & drop ────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, ticket) => {
    setDraggingTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
    e?.dataTransfer?.setData('text/plain', ticket?.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingTicket(null);
    setDropTarget(null);
    setDragOverDay(null);
  }, []);

  const handleDragOver = useCallback((e, dayIdx, hour) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ dayIdx, hour: hour ?? 'allDay' });
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e?.currentTarget?.contains(e?.relatedTarget)) {
      setDropTarget(null);
    }
  }, []);

  const handleDrop = useCallback((e, targetDate, hour) => {
    e?.preventDefault();
    if (!draggingTicket) return;
    const newDate = new Date(targetDate);
    if (hour === 'allDay' || hour == null) {
      newDate?.setHours(0, 0, 0, 0);
    } else {
      newDate?.setHours(hour, 0, 0, 0);
    }
    const oldDate = draggingTicket?.scheduledDate ? new Date(draggingTicket.scheduledDate) : null;
    const isSameSlot = oldDate &&
      oldDate?.getFullYear() === newDate?.getFullYear() &&
      oldDate?.getMonth() === newDate?.getMonth() &&
      oldDate?.getDate() === newDate?.getDate() &&
      oldDate?.getHours() === newDate?.getHours();
    if (!isSameSlot) {
      setDragSuccess(draggingTicket?.id);
      setTimeout(() => setDragSuccess(null), 1500);
      onTicketReschedule?.(draggingTicket, newDate?.toISOString());
    }
    setDraggingTicket(null);
    setDropTarget(null);
    setDragOverDay(null);
  }, [draggingTicket, onTicketReschedule]);

  const isDropTarget = (dayIdx, hour) => {
    if (!dropTarget) return false;
    return dropTarget?.dayIdx === dayIdx && dropTarget?.hour === (hour ?? 'allDay');
  };

  const isDragging = (ticket) => draggingTicket?.id === ticket?.id;

  const TicketChip = ({ ticket, compact = false }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, ticket)}
      onDragEnd={handleDragEnd}
      onClick={() => !draggingTicket && onTicketClick?.(ticket)}
      className={`text-xs px-1.5 py-0.5 rounded border-l-2 mb-0.5 cursor-grab active:cursor-grabbing truncate transition-all select-none ${
        getPriorityColor(ticket?.priority)
      } ${
        isDragging(ticket) ? 'opacity-40 scale-95' : 'hover:opacity-80'
      } ${
        dragSuccess === ticket?.id ? 'ring-2 ring-success' : ''
      }`}
      title={`${ticket?.ticketId} - ${ticket?.customer} (${ticket?.status}) · Drag to reschedule`}
    >
      {compact ? (
        <span className="font-medium">{ticket?.ticketId}</span>
      ) : (
        <>
          <div className="font-medium truncate">{ticket?.ticketId}</div>
          <div className="truncate opacity-80">{ticket?.customer}</div>
          {ticket?.technicians?.length > 0 && (
            <div className="truncate opacity-70 flex items-center gap-0.5">
              <Icon name="User" size={9} />
              {ticket?.technicians?.[0]}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ── Day View ───────────────────────────────────────────────────────────────
  const renderDayView = () => {
    const isDateToday = isToday(selectedDate);
    const currentHour = new Date()?.getHours();
    return (
      <div className="overflow-y-auto max-h-[500px]">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
              isDateToday ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
            }`}>
              {selectedDate?.getDate()}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{fullDayNames?.[selectedDate?.getDay()]}</p>
              <p className="text-xs text-muted-foreground">{monthNames?.[selectedDate?.getMonth()]} {selectedDate?.getFullYear()}</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-border">
          {HOURS?.map((hour) => {
            const hourTickets = getTicketsForDateAndHour(selectedDate, hour);
            const isCurrentHour = isDateToday && hour === currentHour;
            return (
              <div
                key={hour}
                className={`flex min-h-14 ${
                  isCurrentHour ? 'bg-primary/5' : 'hover:bg-muted/50'
                } transition-smooth`}
                onDragOver={(e) => handleDragOver(e, 0, hour)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, selectedDate, hour)}
              >
                <div className="w-16 flex-shrink-0 px-2 py-2 text-right">
                  <span className={`text-xs ${
                    isCurrentHour ? 'text-primary font-semibold' : 'text-muted-foreground'
                  }`}>{formatHour(hour)}</span>
                </div>
                <div className={`flex-1 px-2 py-1 border-l border-border ${
                  isDropTarget(0, hour) ? 'bg-primary/20 ring-2 ring-inset ring-primary/50' : ''
                }`}>
                  {isCurrentHour && <div className="w-full h-0.5 bg-primary mb-1 rounded" />}
                  {hourTickets?.map((ticket, idx) => (
                    <TicketChip key={idx} ticket={ticket} />
                  ))}
                  {isDropTarget(0, hour) && hourTickets?.length === 0 && (
                    <div className="h-full min-h-[40px] flex items-center justify-center">
                      <span className="text-xs text-primary/70 font-medium">Drop here</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Week View ──────────────────────────────────────────────────────────────
  const renderWeekView = () => {
    const weekDays = getWeekDays(selectedDate);
    const today = new Date();
    const currentHour = today?.getHours();
    return (
      <div className="overflow-auto max-h-[500px]">
        <div className="min-w-[560px]">
          {/* Week header */}
          <div className="sticky top-0 z-10 bg-card border-b border-border grid grid-cols-8">
            <div className="w-14" />
            {weekDays?.map((day, idx) => {
              const isCurrentDay = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);
              return (
                <div
                  key={idx}
                  className={`py-2 px-1 text-center cursor-pointer hover:bg-muted/50 transition-smooth ${
                    isSelected ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => setSelectedDate(new Date(day))}
                >
                  <p className="text-xs text-muted-foreground">{dayNames?.[day?.getDay()]}</p>
                  <div className={`mx-auto mt-1 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isCurrentDay ? 'bg-primary text-primary-foreground' :
                    isSelected ? 'bg-primary/20 text-primary' : 'text-foreground'
                  }`}>
                    {day?.getDate()}
                  </div>
                </div>
              );
            })}
          </div>
          {/* All-day row */}
          <div className="grid grid-cols-8 border-b border-border">
            <div className="w-14 px-2 py-1 text-right">
              <span className="text-xs text-muted-foreground">All day</span>
            </div>
            {weekDays?.map((day, dIdx) => {
              const untimedTickets = getUntimedTicketsForDate(day);
              return (
                <div
                  key={dIdx}
                  className={`border-l border-border p-1 min-h-[32px] transition-colors ${
                    isToday(day) ? 'bg-primary/5' : ''
                  } ${
                    isDropTarget(dIdx, null) ? 'bg-primary/20 ring-2 ring-inset ring-primary/50' : ''
                  }`}
                  onDragOver={(e) => handleDragOver(e, dIdx, null)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, day, 'allDay')}
                >
                  {untimedTickets?.map((ticket, tIdx) => (
                    <TicketChip key={tIdx} ticket={ticket} compact />
                  ))}
                </div>
              );
            })}
          </div>
          {/* Hourly rows */}
          <div className="divide-y divide-border">
            {HOURS?.map((hour) => (
              <div key={hour} className="grid grid-cols-8 min-h-[52px]">
                <div className="w-14 px-2 py-1 text-right">
                  <span className="text-xs text-muted-foreground">{formatHour(hour)}</span>
                </div>
                {weekDays?.map((day, dIdx) => {
                  const hourTickets = getTicketsForDateAndHour(day, hour);
                  const isCurrentHourToday = isSameDay(day, today) && hour === currentHour;
                  return (
                    <div
                      key={dIdx}
                      className={`border-l border-border px-1 py-0.5 ${
                        isCurrentHourToday ? 'bg-primary/5' : 'hover:bg-muted/30'
                      } ${
                        isDropTarget(dIdx, hour) ? 'bg-primary/20 ring-2 ring-inset ring-primary/50' : ''
                      } transition-smooth`}
                      onDragOver={(e) => handleDragOver(e, dIdx, hour)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, day, hour)}
                    >
                      {isCurrentHourToday && <div className="w-full h-0.5 bg-primary mb-0.5 rounded" />}
                      {hourTickets?.map((ticket, idx) => (
                        <TicketChip key={idx} ticket={ticket} compact />
                      ))}
                      {isDropTarget(dIdx, hour) && hourTickets?.length === 0 && (
                        <div className="h-full min-h-[40px] flex items-center justify-center">
                          <span className="text-xs text-primary/70 font-medium">Drop</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Month View ─────────────────────────────────────────────────────────────
  const renderMonthView = () => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    const firstDay = new Date(year, month, 1)?.getDay();
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const today = new Date();

    return (
      <div>
        {/* Day name headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {dayNames?.map((d) => (
            <div key={d} className="p-2 text-center bg-muted/50">
              <span className="text-xs font-medium text-muted-foreground">{d}</span>
            </div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7">
          {Array.from({ length: totalCells })?.map((_, i) => {
            const day = i - firstDay + 1;
            const isValid = day > 0 && day <= daysInMonth;
            const dayTickets = isValid ? getTicketsForMonthDay(day) : [];
            const isTodayDay = isValid && today?.getFullYear() === year && today?.getMonth() === month && today?.getDate() === day;
            const isSelectedDay = isValid && selectedDate?.getFullYear() === year && selectedDate?.getMonth() === month && selectedDate?.getDate() === day;
            const isDragOver = dragOverDay === day && isValid;

            return (
              <div
                key={i}
                className={`min-h-16 p-1 border border-border transition-smooth ${
                  isValid ? 'bg-card cursor-pointer hover:bg-muted' : 'bg-muted/50'
                } ${
                  isSelectedDay ? 'ring-2 ring-primary' : ''
                } ${
                  isDragOver ? 'bg-primary/10 ring-2 ring-primary ring-dashed' : ''
                }`}
                onClick={() => isValid && setSelectedDate(new Date(year, month, day))}
                onDragOver={(e) => {
                  if (!isValid) return;
                  e?.preventDefault();
                  setDragOverDay(day);
                }}
                onDragLeave={() => setDragOverDay(null)}
                onDrop={(e) => {
                  if (!isValid) return;
                  e?.preventDefault();
                  setDragOverDay(null);
                  if (!draggingTicket) return;
                  const newDate = new Date(year, month, day);
                  onTicketReschedule?.(draggingTicket, newDate?.toISOString());
                  setDraggingTicket(null);
                }}
              >
                {isValid && (
                  <>
                    <div className={`text-xs font-medium mb-0.5 ${
                      isTodayDay
                        ? 'w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto'
                        : 'text-foreground'
                    }`}>
                      {day}
                    </div>
                    <div className="space-y-0.5">
                      {dayTickets?.slice(0, 2)?.map((ticket, idx) => (
                        <div
                          key={idx}
                          draggable
                          onDragStart={(e) => handleDragStart(e, ticket)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => { e?.stopPropagation(); onTicketClick?.(ticket); }}
                          className={`text-xs p-0.5 rounded truncate cursor-pointer transition-smooth ${
                            ticket?.status?.toLowerCase() === 'completed' ? 'bg-success/10 text-success hover:bg-success/20' :
                            ticket?.status?.toLowerCase() === 'in progress'? 'bg-warning/10 text-warning hover:bg-warning/20' : 'bg-primary/10 text-primary hover:bg-primary/20'
                          }`}
                        >
                          {ticket?.ticketId}
                        </div>
                      ))}
                      {dayTickets?.length > 2 && (
                        <div className="text-xs text-muted-foreground">+{dayTickets?.length - 2}</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        {/* Title + nav row */}
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="CalendarDays" size={16} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-base font-heading font-semibold text-foreground">{getHeaderLabel()}</h2>
              <p className="text-xs text-muted-foreground">
                {totalScheduled} job{totalScheduled !== 1 ? 's' : ''} scheduled
                {onTicketReschedule && <span className="ml-1 text-primary">· Drag to reschedule</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="outline" size="sm" iconName="ChevronLeft" onClick={handlePrev} />
            <Button variant="outline" size="sm" onClick={handleToday}>Today</Button>
            <Button variant="outline" size="sm" iconName="ChevronRight" onClick={handleNext} />
          </div>
        </div>

        {/* View mode switcher */}
        <div className="flex items-center gap-1">
          {['day', 'week', 'month']?.map((mode) => (
            <Button
              key={mode}
              variant={viewMode === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode(mode)}
              className="flex-1 sm:flex-none h-7 sm:h-9 text-xs sm:text-sm px-2 sm:px-3 capitalize"
            >
              {mode?.charAt(0)?.toUpperCase() + mode?.slice(1)}
            </Button>
          ))}
        </div>
      </div>
      {/* Drag hint */}
      {draggingTicket && (
        <div className="px-4 py-2 bg-primary/10 border-b border-primary/20 flex items-center gap-2">
          <Icon name="Move" size={14} color="var(--color-primary)" />
          <span className="text-xs text-primary font-medium">
            Moving <strong>{draggingTicket?.ticketId}</strong> — drop on any slot to reschedule
          </span>
        </div>
      )}
      {/* Views */}
      {viewMode === 'day' && renderDayView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'month' && renderMonthView()}
      {/* Legend */}
      <div className="p-3 border-t border-border bg-muted/30 flex flex-wrap gap-3">
        <span className="text-xs text-muted-foreground font-medium">Priority:</span>
        {[['Critical','bg-red-500/15 border-red-500 text-red-700'],['High','bg-orange-500/15 border-orange-500 text-orange-700'],['Medium','bg-yellow-500/15 border-yellow-500 text-yellow-700'],['Low','bg-green-500/15 border-green-500 text-green-700']]?.map(([label, cls]) => (
          <span key={label} className={`text-xs px-2 py-0.5 rounded border-l-2 ${cls}`}>{label}</span>
        ))}
        <span className="text-xs text-muted-foreground font-medium ml-2">Status:</span>
        {[['Completed','bg-success/15 border-success text-success'],['In Progress','bg-warning/15 border-warning text-warning'],['Pending','bg-muted border-muted-foreground text-muted-foreground']]?.map(([label, cls]) => (
          <span key={label} className={`text-xs px-2 py-0.5 rounded border-l-2 ${cls}`}>{label}</span>
        ))}
      </div>
    </div>
  );
};

export default WeeklyCalendar;
